import React, { useEffect, useRef } from "react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import OrderModificationSummaryComponent from "../../components/OrderModificationSummary/OrderModificationSummary";
import { useOrderModificationSummary } from "hooks/apiHooks";
import CircularLoader from "components/CircularLoader";
import ErrorState from "components/ErrorState/ErrorState";
import { isValidUuid, buildFlowSearchFromParams } from "utils/commonUtils";
import PageNotFound from "components/ErrorHandlers/PageNotFound/PageNotFound";
import routes from "constants/routes";

import styles from "./OrderModificationSummary.module.scss";
import { ORDER_PROPOSAL_STATUS } from "constants/config";

const OrderModificationSummary = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId");

  const isValidEventId = isValidUuid(eventId);
  const acceptedAutoNavOnceRef = useRef(false);
  const rejectedAutoNavOnceRef = useRef(false);

  const {
    data: orderModificationSummaryRows,
    isPending: isOrderModificationLoading,
    isFetching: isOrderModificationFetching,
    error: orderModificationError,
  } = useOrderModificationSummary(eventId, {
    enabled: isValidEventId,
  });

  useEffect(() => {
    if (!orderModificationSummaryRows) return;

    const search = buildFlowSearchFromParams(searchParams);
    const statusId = orderModificationSummaryRows?.proposal_order?.status?.id;

    if (statusId === ORDER_PROPOSAL_STATUS.ACCEPTED) {
      if (acceptedAutoNavOnceRef.current) {
        return;
      }
      acceptedAutoNavOnceRef.current = true;
      const proposedOrder = orderModificationSummaryRows?.proposal_order;
      const modifiedOrderSummaryPathWithQuery = `${routes.modifiedOrderSummary}${search}`;
      navigate(modifiedOrderSummaryPathWithQuery, { state: { proposedOrder } });
      return;
    }
    acceptedAutoNavOnceRef.current = false;

    if (
      [
        ORDER_PROPOSAL_STATUS.REJECTED_BY_CUSTOMER,
        ORDER_PROPOSAL_STATUS.REJECTED_BY_CUSTOMER_SUPPORT,
        ORDER_PROPOSAL_STATUS.DISCARDED,
      ].includes(statusId)
    ) {
      if (rejectedAutoNavOnceRef.current) {
        return;
      }
      rejectedAutoNavOnceRef.current = true;
      const rejectedOrderModificationPathWithQuery = `${routes.rejectedOrderModification}${search}`;
      navigate(rejectedOrderModificationPathWithQuery);
      return;
    }
    rejectedAutoNavOnceRef.current = false;
  }, [orderModificationSummaryRows, navigate, searchParams]);

  if (!isValidEventId) {
    return <PageNotFound />;
  }

  if (isOrderModificationLoading || isOrderModificationFetching) {
    return <CircularLoader />;
  }

  if (orderModificationError) {
    return <ErrorState message={orderModificationError?.message} />;
  }

  return (
    <div className={styles.orderModificationSummaryMainContainer}>
      <OrderModificationSummaryComponent
        orderModificationSummary={orderModificationSummaryRows}
      />
    </div>
  );
};

export default OrderModificationSummary;
